import { useEffect, useMemo, useState } from "react";
import { ExternalLink, RefreshCw, Newspaper, Loader2 } from "lucide-react";
import { getNotifications, refreshNotifications } from "@/lib/api";
import { toast } from "sonner";
import ArticleModal from "@/components/ArticleModal";

const SOURCE_OPTIONS = [
  { key: "", label: "All" },
  { key: "Curated", label: "Curated" },
  { key: "MCA", label: "MCA" },
  { key: "SAG Infotech", label: "SAG Infotech" },
];

const SOURCE_COLORS = {
  Curated: "text-[#002FA7]",
  MCA: "text-emerald-700",
  "SAG Infotech": "text-orange-700",
};

// Pre-computed at module load so the stats row doesn't allocate a new array
// on every render.
const VISIBLE_SOURCES = SOURCE_OPTIONS.filter((s) => s.key);

const curatedCount = (counts, items, key) =>
  counts[key] ?? (key === "Curated" ? items.filter((i) => i.source === "Curated").length : 0);

export default function NotificationsFeed({ limit = 8, fullPage = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [source, setSource] = useState("");
  const [counts, setCounts] = useState({});
  const [refreshedAt, setRefreshedAt] = useState("");
  const [openItem, setOpenItem] = useState(null);

  const load = async () => {
    try {
      const r = await getNotifications();
      setItems(r.data?.items || []);
      setCounts(r.data?.counts_per_source || {});
      setRefreshedAt(r.data?.refreshed_at || "");
    } finally {
      setLoading(false);
    }
  };

  const forceRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshNotifications();
      toast.success("Notifications refreshed");
      await load();
    } catch {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!source) return items;
    return items.filter((i) => i.source === source);
  }, [items, source]);

  const visible = fullPage ? filtered : filtered.slice(0, limit);

  return (
    <div className={`border border-zinc-200 bg-white ${fullPage ? "p-8" : "p-6"}`} data-testid="notifications-feed">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Newspaper size={16} strokeWidth={1.5} className="text-[#002FA7]" />
          <h3 className="font-heading text-lg font-medium tracking-tight">Compliance Updates</h3>
          {refreshedAt && (
            <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-400">
              · refreshed {new Date(refreshedAt).toLocaleDateString()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {fullPage && (
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="border border-zinc-300 px-2 py-1 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              data-testid="notifications-source"
            >
              {SOURCE_OPTIONS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}{s.key && counts[s.key] !== undefined ? ` (${counts[s.key]})` : ""}
                </option>
              ))}
            </select>
          )}
          <button
            onClick={forceRefresh}
            disabled={refreshing}
            className="text-xs text-zinc-500 hover:text-[#002FA7] flex items-center gap-1"
            data-testid="notifications-refresh"
          >
            <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* Source counts strip — full page only */}
      {fullPage && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 mb-6" data-testid="source-counts">
          {VISIBLE_SOURCES.map((s) => (
            <div key={s.key} className="bg-white p-3">
              <div className="label-eyebrow">{s.label}</div>
              <div className={`font-heading text-2xl font-light tracking-tight mt-1 ${SOURCE_COLORS[s.key] || ""}`}>
                {curatedCount(counts, items, s.key)}
              </div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="py-6 text-center text-zinc-500"><Loader2 size={14} className="inline animate-spin" /></div>
      ) : visible.length === 0 ? (
        <div className="text-sm text-zinc-500 py-6 text-center">
          No notifications match. {fullPage && <button onClick={() => setSource("")} className="text-[#002FA7] hover:underline">Clear filter</button>}
        </div>
      ) : (
        <ul className="space-y-4" data-testid="notifications-list">
          {visible.map((it, i) => {
            const isInApp = it.source === "SAG Infotech" || it.source === "Curated" || !!it.body;
            return (
              <li key={`${it.source}-${it.date}-${it.url || it.title}`} className="border-l-2 pl-4 border-zinc-200 hover:border-[#002FA7] transition-colors" data-testid={`notification-item-${i}`}>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1 flex-wrap">
                  <span className="font-mono-plex">{it.date}</span>
                  <span>·</span>
                  <span>{it.category}</span>
                  {it.source && <>
                    <span>·</span>
                    <span className={SOURCE_COLORS[it.source] || ""}>{it.source}</span>
                  </>}
                </div>
                {isInApp ? (
                  <button
                    type="button"
                    onClick={() => setOpenItem(it)}
                    className="font-medium text-sm hover:text-[#002FA7] text-left"
                    data-testid={`notification-open-${i}`}
                  >
                    {it.title}
                  </button>
                ) : (
                  <a
                    href={it.url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-sm hover:text-[#002FA7] inline-flex items-center gap-1"
                    data-testid={`notification-link-${i}`}
                  >
                    {it.title} <ExternalLink size={11} />
                  </a>
                )}
                {it.summary && it.summary !== it.title && (
                  <p className="text-xs text-zinc-600 mt-1 leading-relaxed">{it.summary}</p>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <ArticleModal item={openItem} onClose={() => setOpenItem(null)} />
    </div>
  );
}
